import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  Crown,
  Filter,
  MessageSquare,
  Download,
} from "lucide-react";

import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { useCompanyAdminAuth } from "../../contexts/CompanyAdminAuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { companyAdminCustomersAPI } from "../../lib/companyAdminApi";
import { getCustomerFlagMeta } from "../../lib/customerRegionFlag";
import { getWorldCountryOptions } from "../../lib/worldCountries";
import CustomerMembershipBadge from "../../components/CustomerMembershipBadge";

const WORLD_COUNTRIES = getWorldCountryOptions();

const emptyForm = {
  name: "",
  email: "",
  password: "",
  phone_number: "",
  status: "active",
  group_id: "",
};

const PLAN_LABELS = { 1: "Strict", 2: "Moderate", 3: "Flexible Diet Plan" };

const DEFAULT_WEEKLY_CHECKIN =
  "Hi! This is your weekly check-in. How has your week been with meals and goals? Reply anytime — I'm here to help.";

const TABS = [
  { key: "all", label: "All" },
  { key: "unpaid", label: "Unpaid" },
  { key: "ai_plan", label: "AI Plan" },
  { key: "premium", label: "Premium Members" },
  { key: "expired", label: "Expired Members" },
];

/* ── Format last seen date like "18-03-2026 (2 hrs)" ── */
function formatLastSeen(dateVal) {
  if (!dateVal) return null;
  const date = new Date(dateVal);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const dateStr = `${dd}-${mm}-${yyyy}`;

  let ago;
  if (diffMins < 1) ago = "just now";
  else if (diffMins < 60) ago = `${diffMins} min${diffMins > 1 ? "s" : ""}`;
  else if (diffHrs < 24) ago = `${diffHrs} hr${diffHrs > 1 ? "s" : ""}`;
  else if (diffDays === 1) ago = "1 day";
  else ago = `${diffDays} days`;

  return { dateStr, ago };
}

/* ── Days since last food log (null / missing = never logged → very inactive) ── */
function getDaysSinceLastFoodLog(lastIntakeDate) {
  if (!lastIntakeDate) return Number.POSITIVE_INFINITY;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const intake = new Date(lastIntakeDate);
  intake.setHours(0, 0, 0, 0);
  return Math.round((today - intake) / 86400000);
}

/** At-risk: no food log for 2+ days (or never logged). */
function isAtRiskFoodInactive(lastIntakeDate) {
  return getDaysSinceLastFoodLog(lastIntakeDate) >= 2;
}

/* ── Eye icon color based on last diet intake date ── */
function getEyeColor(lastIntakeDate) {
  const diffDays = getDaysSinceLastFoodLog(lastIntakeDate);
  if (!Number.isFinite(diffDays)) return "text-red-500";
  if (diffDays === 0) return "text-green-500";
  if (diffDays === 1) return "text-yellow-500";
  if (diffDays >= 2) return "text-red-500";
  return "text-slate-400";
}

function formatKg(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const n = Number(value);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} kg`;
}

function formatLastLoggedFood(dateVal) {
  if (!dateVal) return null;
  const date = new Date(dateVal);
  if (Number.isNaN(date.getTime())) return null;
  // Client format: "19 Jun 2026"
  const dateStr = date
    .toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(/,/g, "");
  const days = getDaysSinceLastFoodLog(dateVal);
  let ago = "";
  if (!Number.isFinite(days)) ago = "";
  else if (days === 0) ago = "Today";
  else if (days === 1) ago = "1 day ago";
  else ago = `${days} days ago`;
  return { dateStr, ago };
}

const StatCard = ({ icon: Icon, label, value, color, loading }) => (
  <div className="panel border border-border bg-card p-3 sm:p-5 flex items-center gap-3 sm:gap-4">
    <div
      className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}
    >
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] sm:text-xs text-muted-foreground font-medium truncate">
        {label}
      </div>
      <div className="text-xl sm:text-2xl font-bold text-foreground mt-0.5">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : (
          (value ?? 0)
        )}
      </div>
    </div>
  </div>
);

// Returns Tailwind text color class based on customer active plan
const getPlanNameColor = (c) => {
  // At-risk food inactivity wins — red name for coach visibility
  if (isAtRiskFoodInactive(c.last_diet_intake_date)) {
    return "text-red-600 font-semibold";
  }
  const now = Date.now();
  // Premium: green
  if (Number(c.premium_member) === 1) {
    const exp = c.expiry_date ? new Date(c.expiry_date).getTime() : Infinity;
    if (exp > now) return "text-green-600 font-semibold";
  }
  // AI Plan Paid (non-trial, active): blue
  if (
    Number(c.ai_plan) === 1 &&
    Number(c.trial) !== 1 &&
    c.ai_plan_expiry &&
    new Date(c.ai_plan_expiry).getTime() > now
  ) {
    return "text-blue-600 font-semibold";
  }
  // Trial: black
  if (Number(c.trial) === 1) {
    return "text-foreground font-medium";
  }
  // Unpaid / expired: red
  return "text-red-600 font-medium";
};

function DeviceGlyph({ customer }) {
  const dt =
    customer.device_type ||
    (customer.loginType === "apple" ? "ios" : null);
  if (dt === "ios") {
    return (
      <span title="iOS" className="inline-flex shrink-0 text-[#555]">
        <svg width="12" height="12" viewBox="0 0 814 1000" fill="currentColor">
          <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.1 268.1-317.1 71 0 130.5 46.4 175.1 46.4 42.8 0 109.5-49.1 190.5-49.1zm-84.7-236.5c39.5-47.1 68.2-112.4 68.2-177.7 0-9-1.3-18-2.6-26.4-64.7 2.6-140.8 43.5-186.5 98.2-35.8 41.5-70.2 106.8-70.2 173.5 0 9.6 1.3 19.3 2.5 22.3 3.8.6 10.3 1.3 16.7 1.3 58.2 0 128.2-39.5 172-91.2z" />
        </svg>
      </span>
    );
  }
  if (dt === "android") {
    return (
      <span title="Android" className="inline-flex shrink-0 text-[#3ddc84]">
        <svg width="12" height="12" viewBox="0 0 576 512" fill="currentColor">
          <path d="M420.55,301.93a24,24,0,1,1,24-24,24,24,0,0,1-24,24m-265.1,0a24,24,0,1,1,24-24,24,24,0,0,1-24,24m273.7-144.48,47.94-83a10,10,0,1,0-17.27-10h0l-48.54,84.07a301.25,301.25,0,0,0-246.56,0L116.18,64.45a10,10,0,1,0-17.27,10h0l47.94,83C64.53,202.22,8.24,285.55,0,384H576c-8.24-98.45-64.54-181.78-146.85-226.55" />
        </svg>
      </span>
    );
  }
  return null;
}

const CustomersPage = () => {
  const { user, canAction } = useCompanyAdminAuth();
  const canAdd = canAction("customers", "add");
  const canEdit = canAction("customers", "edit");
  const canDelete = canAction("customers", "delete");
  const isAgent = Number(user?.user_type) === 3;

  const [activeTab, setActiveTab] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  const [overviewStats, setOverviewStats] = useState(null);
  const navigate = useNavigate();
  const [statsLoading, setStatsLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [lastSeenFilter, setLastSeenFilter] = useState("");
  const [inactiveFoodFilter, setInactiveFoodFilter] = useState("");
  const [bmiCategoryFilter, setBmiCategoryFilter] = useState("");
  const [healthConditionFilter, setHealthConditionFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [signupFrom, setSignupFrom] = useState("");
  const [signupTo, setSignupTo] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    customer: null,
    deleting: false,
  });
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState(
    DEFAULT_WEEKLY_CHECKIN,
  );
  const [bulkSending, setBulkSending] = useState(false);

  const [groups, setGroups] = useState([]);

  const isEdit = useMemo(() => !!editingCustomer, [editingCustomer]);
  const selectedCount = selectedIds.size;
  const pageIds = useMemo(
    () => customers.map((c) => String(c.user_id ?? c._id)).filter(Boolean),
    [customers],
  );
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected =
    pageIds.some((id) => selectedIds.has(id)) && !allPageSelected;

  const fetchGroups = async () => {
    try {
      const res = await companyAdminCustomersAPI.listGroups({
        include_inactive: false,
      });
      setGroups(res.data.groups || []);
    } catch {
      setGroups([]);
    }
  };

  const buildParams = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (planFilter) params.diet_plan_type = planFilter;
    if (partnerFilter) params.partner_id = partnerFilter;
    if (search) params.search = search;
    if (lastSeenFilter) params.last_seen_days = lastSeenFilter;
    if (inactiveFoodFilter) params.inactive_food_days = inactiveFoodFilter;
    if (bmiCategoryFilter) params.bmi_category = bmiCategoryFilter;
    if (healthConditionFilter.trim()) {
      params.health_condition = healthConditionFilter.trim();
    }
    if (countryFilter) params.country = countryFilter;
    if (signupFrom) params.signup_from = signupFrom;
    if (signupTo) params.signup_to = signupTo;
    if (activeTab !== "all") params.member_tab = activeTab;
    return params;
  };

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) pageIds.forEach((id) => next.add(id));
      else pageIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const openBulkMessage = () => {
    if (selectedCount === 0) {
      toast.info("Select at least one customer");
      return;
    }
    setBulkMessageText(DEFAULT_WEEKLY_CHECKIN);
    setBulkMessageOpen(true);
  };

  const handleBulkMessage = async () => {
    const text = bulkMessageText.trim();
    if (!text) {
      toast.error("Enter a message");
      return;
    }
    setBulkSending(true);
    try {
      const res = await companyAdminCustomersAPI.bulkMessage({
        user_ids: Array.from(selectedIds),
        text,
      });
      const sent = res.data?.sent ?? 0;
      const failed = res.data?.failed?.length ?? 0;
      if (sent > 0) {
        toast.success(
          `Sent check-in to ${sent} customer${sent !== 1 ? "s" : ""}${
            failed ? ` (${failed} failed)` : ""
          }`,
        );
        setSelectedIds(new Set());
        setBulkMessageOpen(false);
      } else {
        toast.error(
          failed
            ? `Could not send message (${failed} failed)`
            : "Could not send message",
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Bulk message failed");
    } finally {
      setBulkSending(false);
    }
  };

  const fetchOverviewStats = async () => {
    setStatsLoading(true);
    try {
      const res =
        await companyAdminCustomersAPI.getCustomersOverviewStats(buildParams());
      setOverviewStats(res.data);
    } catch {
      setOverviewStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { include_inactive: true, ...buildParams() };
      const res = await companyAdminCustomersAPI.exportCustomers(params);
      const rows = res.data.data || [];
      if (rows.length === 0) {
        toast.info("No customers to export");
        return;
      }

      const headers = [
        "Name",
        "Email",
        "Phone",
        "Status",
        "Diet Plan",
        "Group",
        "Partner",
        "Premium",
        "AI Plan",
        "Signup Date",
        "Last Seen",
        "Expiry Date",
      ];
      const fields = [
        "name",
        "email",
        "phone_number",
        "status",
        "diet_plan",
        "group",
        "partner",
        "premium_member",
        "ai_plan",
        "signup_date",
        "last_seen",
        "expiry_date",
      ];

      const escape = (val) => {
        const s = String(val ?? "");
        return s.includes(",") || s.includes('"') || s.includes("\n")
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };

      const csvLines = [
        headers.join(","),
        ...rows.map((row) => fields.map((f) => escape(row[f])).join(",")),
      ];
      const blob = new Blob([csvLines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `Exported ${rows.length} customer${rows.length !== 1 ? "s" : ""} to CSV`,
      );
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        include_inactive: true,
        ...buildParams(),
      };
      if (activityFilter) params.sort_by = activityFilter;
      const res = await companyAdminCustomersAPI.getAll(params);
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchOverviewStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    search,
    statusFilter,
    planFilter,
    partnerFilter,
    activityFilter,
    lastSeenFilter,
    inactiveFoodFilter,
    bmiCategoryFilter,
    healthConditionFilter,
    countryFilter,
    signupFrom,
    signupTo,
    activeTab,
  ]);

  useEffect(() => {
    fetchGroups();
    companyAdminCustomersAPI
      .getPartnersList()
      .then((res) => setPartners(res.data?.partners || res.data || []))
      .catch(() => setPartners([]));
  }, []); // eslint-disable-line

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  const openCreate = () => {
    setEditingCustomer(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || "",
      email: customer.email || "",
      password: "",
      phone_number: customer.phone_number || "",
      status: customer.status || "active",
      group_id: customer.group_id ?? customer.group?.id ?? "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCustomer(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const emailTrimmed = form.email.trim();
    // Email required only when creating; when editing it's optional (mobile users may have none)
    if (!isEdit && !emailTrimmed) return toast.error("Email is required");
    if (emailTrimmed) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrimmed))
        return toast.error("Enter a valid email address");
    }
    if (!isEdit && !form.password.trim()) {
      return toast.error("Password is required");
    }
    setSaving(true);
    try {
      if (isEdit) {
        const updatePayload = {
          name: form.name,
          phone_number: form.phone_number,
          status: form.status,
          group_id: form.group_id === "" ? null : form.group_id,
        };
        if (emailTrimmed) updatePayload.email = emailTrimmed.toLowerCase();
        await companyAdminCustomersAPI.updateBasic(
          editingCustomer.user_id,
          updatePayload,
        );
        toast.success("Customer updated");
      } else {
        await companyAdminCustomersAPI.create({
          name: form.name,
          email: form.email,
          password: form.password,
          phone_number: form.phone_number,
        });
        toast.success("Customer created");
      }
      closeModal();
      fetchCustomers();
      fetchOverviewStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (customer) =>
    setDeleteModal({ open: true, customer, deleting: false });
  const closeDeleteModal = () =>
    setDeleteModal({ open: false, customer: null, deleting: false });

  const handleDelete = async (hard) => {
    const customer = deleteModal.customer;
    if (!customer) return;
    setDeleteModal((d) => ({ ...d, deleting: true }));
    try {
      await companyAdminCustomersAPI.delete(customer.user_id, hard);
      toast.success(
        hard ? "Customer permanently deleted" : "Customer deactivated",
      );
      closeDeleteModal();
      fetchCustomers();
      fetchOverviewStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setDeleteModal((d) => ({ ...d, deleting: false }));
    }
  };

  const hasFilters =
    statusFilter ||
    planFilter ||
    search ||
    partnerFilter ||
    activityFilter ||
    lastSeenFilter ||
    inactiveFoodFilter ||
    bmiCategoryFilter ||
    healthConditionFilter ||
    countryFilter ||
    signupFrom ||
    signupTo;

  const openChat = (c) => {
    const gid = c.group_id ?? c.group?.id ?? c.group?._id;
    const uid = c.user_id ?? c._id;
    if (gid) {
      navigate(
        `/admin/chats?group_id=${encodeURIComponent(String(gid))}&customer=${encodeURIComponent(String(uid))}`,
      );
    } else {
      navigate(`/admin/chats?customer=${encodeURIComponent(String(uid))}`);
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="company-admin-customers-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground">
              Customers
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {total} customers found
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-customers-btn"
              className="flex-1 sm:flex-none"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  <span className="sm:inline">Export CSV</span>
                </>
              )}
            </Button>
            {canAdd && (
              <Button
                onClick={openCreate}
                data-testid="add-customer-btn"
                className="flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            )}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
          <StatCard
            icon={Users}
            label="Total Customers"
            value={overviewStats?.total}
            color="bg-blue-500"
            loading={statsLoading}
          />
          <StatCard
            icon={TrendingUp}
            label="New This Month"
            value={overviewStats?.newThisMonth}
            color="bg-indigo-500"
            loading={statsLoading}
          />
          <StatCard
            icon={UserCheck}
            label="Active"
            value={overviewStats?.active}
            color="bg-green-500"
            loading={statsLoading}
          />
          <StatCard
            icon={UserX}
            label="Inactive"
            value={overviewStats?.inactive}
            color="bg-red-400"
            loading={statsLoading}
          />
          <StatCard
            icon={Crown}
            label="Premium"
            value={overviewStats?.premium}
            color="bg-amber-500"
            loading={statsLoading}
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-border -mx-3 px-3 sm:mx-0 sm:px-0">
          <nav
            className="flex gap-0 overflow-x-auto scrollbar-thin"
            data-testid="customer-tabs"
          >
            {TABS.map((tab) => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => handleTabChange(tab.key)}
                className={`px-3 sm:px-5 py-2.5 sm:py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search & Filters */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
                data-testid="company-admin-customer-search"
              />
            </div>
            <div className="relative w-full sm:max-w-sm">
              <Input
                placeholder="Filter by health conditions..."
                value={healthConditionFilter}
                onChange={(e) => {
                  setHealthConditionFilter(e.target.value);
                  setPage(1);
                }}
                data-testid="customer-health-condition-filter"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-stretch sm:items-center gap-2">
            <div className="col-span-2 sm:col-span-1 flex items-center gap-2 text-muted-foreground sm:contents">
              <Filter className="w-4 h-4 shrink-0 hidden sm:block" />
              <span className="sm:hidden text-xs font-medium uppercase tracking-wide">
                Filters
              </span>
            </div>
            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-status-filter"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-plan-filter"
            >
              <option value="">All Plans</option>
              <option value="1">Strict</option>
              <option value="2">Moderate</option>
              <option value="3">Flexible Diet Plan</option>
            </select>

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={bmiCategoryFilter}
              onChange={(e) => {
                setBmiCategoryFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-bmi-category-filter"
              title="Filter by WHO BMI category"
            >
              <option value="">BMI: All</option>
              <option value="underweight">Underweight</option>
              <option value="overweight">Overweight</option>
              <option value="obese">Obese</option>
            </select>

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto max-w-[220px]"
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-country-filter"
              title="Filter by country (matches country code or timezone)"
            >
              <option value="">All Countries</option>
              {WORLD_COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            {partners.length > 0 && (
              <select
                className="form-select text-sm py-2 px-2 w-full sm:w-auto"
                value={partnerFilter}
                onChange={(e) => {
                  setPartnerFilter(e.target.value);
                  setPage(1);
                }}
                data-testid="customer-partner-filter"
              >
                <option value="">All Partners</option>
                {partners.map((p) => (
                  <option
                    key={String(p._id || p.id)}
                    value={String(p._id || p.id)}
                  >
                    {p.partner_name || p.name}
                  </option>
                ))}
              </select>
            )}

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={activityFilter}
              onChange={(e) => {
                setActivityFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-activity-filter"
            >
              <option value="">All Activity</option>
              <option value="last_intake">By Last Intake</option>
            </select>

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={lastSeenFilter}
              onChange={(e) => {
                setLastSeenFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-last-seen-filter"
            >
              <option value="">Last Seen: All</option>
              <option value="0">Today</option>
              <option value="1">1 day ago</option>
              <option value="2">2 days ago</option>
              <option value="3">3 days ago</option>
              <option value="4">4 days ago</option>
              <option value="5">5 days ago</option>
              <option value="6">6 days ago</option>
              <option value="7">7 days ago</option>
              <option value="7plus">Older than 7 days</option>
            </select>

            <select
              className="form-select text-sm py-2 px-2 w-full sm:w-auto"
              value={inactiveFoodFilter}
              onChange={(e) => {
                setInactiveFoodFilter(e.target.value);
                setPage(1);
              }}
              data-testid="customer-inactive-food-filter"
              title="Customers who have not logged food for this many days"
            >
              <option value="">No food log: All</option>
              <option value="2">No food log: 2+ days</option>
              <option value="3">No food log: 3+ days</option>
              <option value="4">No food log: 4+ days</option>
            </select>

            <div className="col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-card border border-input rounded-md px-2 py-2 text-sm sm:w-auto">
              <span className="text-muted-foreground text-xs whitespace-nowrap shrink-0">
                Signup:
              </span>
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="date"
                  className="border-0 outline-none text-sm text-muted-foreground bg-transparent flex-1 min-w-0"
                  value={signupFrom}
                  onChange={(e) => {
                    setSignupFrom(e.target.value);
                    setPage(1);
                  }}
                  data-testid="customer-signup-from"
                />
                <span className="text-muted-foreground text-xs shrink-0">–</span>
                <input
                  type="date"
                  className="border-0 outline-none text-sm text-muted-foreground bg-transparent flex-1 min-w-0"
                  value={signupTo}
                  onChange={(e) => {
                    setSignupTo(e.target.value);
                    setPage(1);
                  }}
                  data-testid="customer-signup-to"
                />
              </div>
            </div>

            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setPlanFilter("");
                  setPartnerFilter("");
                  setActivityFilter("");
                  setLastSeenFilter("");
                  setInactiveFoodFilter("");
                  setBmiCategoryFilter("");
                  setHealthConditionFilter("");
                  setCountryFilter("");
                  setSignupFrom("");
                  setSignupTo("");
                  setPage(1);
                }}
                className="text-muted-foreground hover:text-foreground text-xs col-span-2 sm:col-span-1"
              >
                Clear
              </Button>
            )}
          </div>

          {selectedCount > 0 && (
            <div
              className="flex flex-wrap items-center gap-2 sm:gap-3 rounded-md border border-border bg-muted/40 px-3 py-2"
              data-testid="customer-bulk-toolbar"
            >
              <span className="text-sm text-muted-foreground">
                {selectedCount} selected
              </span>
              <Button
                size="sm"
                onClick={openBulkMessage}
                data-testid="customer-bulk-message-btn"
                className="w-full sm:w-auto"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Send weekly check-in
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                className="text-muted-foreground text-xs"
              >
                Clear selection
              </Button>
            </div>
          )}
        </div>

        {/* List: cards on mobile, table on md+ */}
        <div className="panel border border-border p-0 overflow-hidden bg-card">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground px-4 text-center">
              <Users className="w-12 h-12 mb-4 text-muted-foreground" />
              <p>No customers found</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-border">
                <div className="flex items-center gap-3 px-3 py-2.5 bg-muted/50">
                  <Checkbox
                    checked={
                      allPageSelected
                        ? true
                        : somePageSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(v) => toggleSelectAllPage(v === true)}
                    aria-label="Select all on page"
                    data-testid="customer-select-all-mobile"
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    Select all on page
                  </span>
                </div>
                {customers.map((c) => {
                  const rowId = String(c.user_id ?? c._id);
                  const atRisk = isAtRiskFoodInactive(c.last_diet_intake_date);
                  const flag = getCustomerFlagMeta(c);
                  const current = formatKg(c.current_weight_kg);
                  const target = formatKg(c.target_weight_kg);
                  const bmi =
                    c.bmi != null && Number.isFinite(Number(c.bmi))
                      ? Number(c.bmi).toFixed(1)
                      : null;
                  const ls = formatLastSeen(c.last_seen);
                  const lf = formatLastLoggedFood(c.last_diet_intake_date);
                  const rawSignup = c.created_at || c.createdAt;
                  let signupLabel = null;
                  let signupToday = false;
                  if (rawSignup) {
                    const date = new Date(rawSignup);
                    const today = new Date();
                    signupToday =
                      date.getFullYear() === today.getFullYear() &&
                      date.getMonth() === today.getMonth() &&
                      date.getDate() === today.getDate();
                    signupLabel = date.toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    });
                  }

                  return (
                    <div
                      key={`m-${rowId}`}
                      className="p-3 space-y-3"
                      data-testid={`company-admin-customer-card-${rowId}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedIds.has(rowId)}
                          onCheckedChange={() => toggleSelect(rowId)}
                          aria-label={`Select ${c.name || "customer"}`}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex min-w-0 items-center gap-1.5">
                            {flag && (
                              <span
                                className="shrink-0 text-base leading-none"
                                title={flag.title}
                                role="img"
                                aria-label={flag.title}
                              >
                                {flag.emoji}
                              </span>
                            )}
                            <span
                              className={`truncate text-sm ${getPlanNameColor(c)}`}
                            >
                              {c.name || "-"}
                            </span>
                            {atRisk && (
                              <span
                                className="inline-block h-2 w-2 shrink-0 rounded-full bg-red-500"
                                title={
                                  c.last_diet_intake_date
                                    ? `No food log for ${getDaysSinceLastFoodLog(c.last_diet_intake_date)} days`
                                    : "Never logged food"
                                }
                              />
                            )}
                            <DeviceGlyph customer={c} />
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <CustomerMembershipBadge customer={c} showLabel />
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                c.status === "active"
                                  ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300"
                                  : "bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300"
                              }`}
                            >
                              {c.status}
                            </span>
                          </div>
                          {c.email ? (
                            <div className="truncate text-[11px] text-muted-foreground">
                              {c.email}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs pl-8">
                        <div>
                          <div className="text-muted-foreground">Signup</div>
                          <div className="text-foreground font-medium mt-0.5">
                            {signupLabel ? (
                              signupToday ? (
                                <span className="rounded-full bg-yellow-100 dark:bg-yellow-950/40 px-2 py-0.5 text-xs font-semibold text-yellow-800 dark:text-yellow-200">
                                  {signupLabel}
                                </span>
                              ) : (
                                signupLabel
                              )
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Weight / BMI</div>
                          <div className="text-foreground font-medium mt-0.5">
                            {!current && !target && !bmi
                              ? "-"
                              : `${current || "—"} → ${target || "—"} · BMI ${bmi || "—"}`}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Group</div>
                          <div className="text-foreground font-medium mt-0.5 truncate">
                            {c.group?.name || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Diet plan</div>
                          <div className="text-foreground font-medium mt-0.5">
                            {PLAN_LABELS[Number(c.diet_plan_type)] || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Last seen</div>
                          <div className="text-foreground font-medium mt-0.5">
                            {ls ? `${ls.dateStr} (${ls.ago})` : "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Last food log</div>
                          <div
                            className={`font-medium mt-0.5 ${
                              !lf || atRisk ? "text-red-600" : "text-foreground"
                            }`}
                          >
                            {!lf
                              ? "Never"
                              : `${lf.dateStr} (${lf.ago})`}
                          </div>
                        </div>
                        {c.partner?.partner_name ? (
                          <div className="col-span-2">
                            <div className="text-muted-foreground">Partner</div>
                            <div className="text-foreground font-medium mt-0.5 truncate">
                              {c.partner.partner_name}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-end gap-1 pl-8 border-t border-border pt-2">
                        <Link to={`/admin/customers/${c.user_id || c._id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0"
                            title={
                              c.last_diet_intake_date
                                ? `Last intake: ${new Date(c.last_diet_intake_date).toLocaleDateString()}`
                                : "No recent intake"
                            }
                          >
                            <Eye
                              className={`h-4 w-4 ${getEyeColor(c.last_diet_intake_date)}`}
                            />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 text-blue-500"
                          onClick={() => openChat(c)}
                          title="Open chat"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0"
                            onClick={() => openEdit(c)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0 text-destructive"
                            onClick={() => openDeleteModal(c)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
              <Table className="min-w-[1100px]">
                <TableHeader>
                  <TableRow className="bg-muted hover:bg-muted">
                    <TableHead className="w-12 text-center">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={
                            allPageSelected
                              ? true
                              : somePageSelected
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={(v) =>
                            toggleSelectAllPage(v === true)
                          }
                          aria-label="Select all on page"
                          data-testid="customer-select-all"
                        />
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[220px] text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Name + Plan + Status
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Signup Date
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Weight + BMI
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Group
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Partner
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Diet Plan
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Last Seen
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Last food log date
                    </TableHead>
                    <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((c) => {
                    const rowId = String(c.user_id ?? c._id);
                    const atRisk = isAtRiskFoodInactive(
                      c.last_diet_intake_date,
                    );
                    const flag = getCustomerFlagMeta(c);
                    const current = formatKg(c.current_weight_kg);
                    const target = formatKg(c.target_weight_kg);
                    const bmi =
                      c.bmi != null && Number.isFinite(Number(c.bmi))
                        ? Number(c.bmi).toFixed(1)
                        : null;
                    const ls = formatLastSeen(c.last_seen);
                    const lf = formatLastLoggedFood(c.last_diet_intake_date);
                    const rawSignup = c.created_at || c.createdAt;
                    let signupLabel = null;
                    let signupToday = false;
                    if (rawSignup) {
                      const date = new Date(rawSignup);
                      const today = new Date();
                      signupToday =
                        date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate();
                      signupLabel = date.toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      });
                    }

                    return (
                      <TableRow
                        key={rowId}
                        className="hover:bg-muted"
                        data-testid={`company-admin-customer-row-${rowId}`}
                      >
                        <TableCell className="w-12 text-center align-middle">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={selectedIds.has(rowId)}
                              onCheckedChange={() => toggleSelect(rowId)}
                              aria-label={`Select ${c.name || "customer"}`}
                              data-testid={`customer-select-${rowId}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="align-middle text-left">
                          <div className="min-w-0 space-y-1">
                            <div className="flex min-w-0 items-center gap-1.5">
                              {flag && (
                                <span
                                  className="shrink-0 text-base leading-none"
                                  title={flag.title}
                                  role="img"
                                  aria-label={flag.title}
                                >
                                  {flag.emoji}
                                </span>
                              )}
                              <span
                                className={`truncate text-sm ${getPlanNameColor(c)}`}
                              >
                                {c.name || "-"}
                              </span>
                              {atRisk && (
                                <span
                                  className="inline-block h-2 w-2 shrink-0 rounded-full bg-red-500"
                                  title={
                                    c.last_diet_intake_date
                                      ? `No food log for ${getDaysSinceLastFoodLog(c.last_diet_intake_date)} days`
                                      : "Never logged food"
                                  }
                                  aria-label="At risk — inactive food log"
                                />
                              )}
                              <DeviceGlyph customer={c} />
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <CustomerMembershipBadge
                                customer={c}
                                showLabel
                              />
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  c.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-600"
                                }`}
                              >
                                {c.status}
                              </span>
                            </div>
                            {c.email ? (
                              <div
                                className="truncate text-[11px] text-muted-foreground"
                                title={c.email}
                              >
                                {c.email}
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap text-center text-sm text-muted-foreground">
                          {signupLabel ? (
                            signupToday ? (
                              <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                                {signupLabel}
                              </span>
                            ) : (
                              signupLabel
                            )
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap text-center text-sm">
                          {!current && !target && !bmi ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <div className="inline-flex flex-col items-center leading-snug">
                              <span className="text-muted-foreground">
                                {current || "—"}
                                {target ? (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    →{" "}
                                    <span className="font-medium text-foreground">
                                      {target}
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                BMI: {bmi || "—"}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-middle text-center text-sm text-muted-foreground">
                          {c.group?.name || "-"}
                        </TableCell>
                        <TableCell className="align-middle text-center text-sm text-muted-foreground">
                          {c.partner?.partner_name || "-"}
                        </TableCell>
                        <TableCell className="align-middle text-center text-sm text-muted-foreground">
                          {PLAN_LABELS[Number(c.diet_plan_type)] || "-"}
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap text-center text-sm">
                          {ls ? (
                            <div className="inline-flex flex-col items-center leading-snug">
                              <span className="text-muted-foreground">
                                {ls.dateStr}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                ({ls.ago})
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap text-center text-sm">
                          {!lf ? (
                            <span className="font-medium text-red-500">
                              Never
                            </span>
                          ) : (
                            <div className="inline-flex flex-col items-center leading-snug">
                              <span
                                className={
                                  atRisk
                                    ? "font-medium text-red-600"
                                    : "text-muted-foreground"
                                }
                              >
                                {lf.dateStr}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                ({lf.ago})
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="w-[140px] align-middle text-center">
                          <div className="inline-flex items-center justify-center gap-0.5">
                            <Link
                              to={`/admin/customers/${c.user_id || c._id}`}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                data-testid={`company-admin-view-${c.user_id}`}
                                title={
                                  c.last_diet_intake_date
                                    ? `Last intake: ${new Date(c.last_diet_intake_date).toLocaleDateString()}`
                                    : "No recent intake"
                                }
                              >
                                <Eye
                                  className={`h-4 w-4 ${getEyeColor(c.last_diet_intake_date)}`}
                                />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                              onClick={() => openChat(c)}
                              data-testid={`company-admin-chat-${c.user_id}`}
                              title="Open chat"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => openEdit(c)}
                                data-testid={`company-admin-edit-${c.user_id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => openDeleteModal(c)}
                                data-testid={`company-admin-delete-${c.user_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4 border-t border-border">
              <span className="text-xs sm:text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Customer Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => !saving && closeModal()} role="presentation">
            <div
              className="panel w-full max-w-md mx-0 sm:mx-4 p-0 rounded-b-none sm:rounded-md max-h-[75vh] app-modal-shell flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="company-admin-customer-modal"
            >
              <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">
                  {isEdit ? "Edit Customer" : "Add Customer"}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="app-modal-body p-6 space-y-4">
                <div>
                  <Label className="text-foreground">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="customer-name"
                  />
                </div>
                <div>
                  <Label className="text-foreground">
                    Email{" "}
                    {isEdit ? (
                      <span className="text-muted-foreground font-normal text-xs ml-1">
                        (optional for mobile users)
                      </span>
                    ) : (
                      "*"
                    )}
                  </Label>
                  <Input
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    data-testid="customer-email"
                    placeholder="customer@example.com"
                  />
                </div>
                {!isEdit && (
                  <div>
                    <Label className="text-foreground">Password *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                      data-testid="customer-password"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-foreground">Phone</Label>
                  <Input
                    value={form.phone_number}
                    onChange={(e) =>
                      setForm({ ...form, phone_number: e.target.value })
                    }
                    data-testid="customer-phone"
                  />
                </div>
                {isEdit && (
                  <div>
                    <Label className="text-foreground">Select Group</Label>
                    <select
                      className="form-select"
                      value={form.group_id}
                      onChange={(e) =>
                        setForm({ ...form, group_id: e.target.value })
                      }
                      data-testid="customer-group"
                    >
                      <option value="">No Group</option>
                      {(groups || []).map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-muted-foreground mt-1">
                      Only active groups are available.
                    </div>
                  </div>
                )}
                {isEdit && (
                  <div>
                    <Label className="text-foreground">Status</Label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value })
                      }
                      data-testid="customer-status"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="customer-save"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk weekly check-in */}
        {bulkMessageOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => !bulkSending && setBulkMessageOpen(false)} role="presentation">
            <div
              className="panel w-full max-w-lg mx-0 sm:mx-4 p-0 rounded-b-none sm:rounded-md max-h-[75vh] app-modal-shell flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="customer-bulk-message-modal"
            >
              <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">
                  Weekly check-in
                </h3>
                <button
                  onClick={() => !bulkSending && setBulkMessageOpen(false)}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={bulkSending}
                >
                  ✕
                </button>
              </div>
              <div className="app-modal-body p-6 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Send the same message to{" "}
                  <span className="font-semibold text-foreground">
                    {selectedCount}
                  </span>{" "}
                  selected customer{selectedCount !== 1 ? "s" : ""} via chat.
                </p>
                <div>
                  <Label className="text-foreground">Message</Label>
                  <Textarea
                    value={bulkMessageText}
                    onChange={(e) => setBulkMessageText(e.target.value)}
                    rows={5}
                    className="mt-1.5"
                    data-testid="customer-bulk-message-text"
                  />
                </div>
              </div>
              <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setBulkMessageOpen(false)}
                  disabled={bulkSending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkMessage}
                  disabled={bulkSending || !bulkMessageText.trim()}
                  data-testid="customer-bulk-message-send"
                >
                  {bulkSending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send to {selectedCount}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {deleteModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => !deleteModal.deleting && closeDeleteModal()} role="presentation">
            <div
              className="panel w-full max-w-md mx-0 sm:mx-4 p-0 rounded-b-none sm:rounded-md max-h-[75vh] app-modal-shell flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="customer-delete-modal"
            >
              <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Customer
                </h3>
                <button
                  onClick={closeDeleteModal}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={deleteModal.deleting}
                >
                  ✕
                </button>
              </div>
              <div className="app-modal-body p-6">
                <p className="text-sm text-muted-foreground">
                  How would you like to delete{" "}
                  <span className="font-semibold text-foreground">
                    {deleteModal.customer?.email}
                  </span>
                  ?
                </p>

                <div className="mt-4 space-y-3">
                  <button
                    data-testid="soft-delete-btn"
                    onClick={() => handleDelete(false)}
                    disabled={deleteModal.deleting}
                    className="w-full text-left p-4 rounded-lg border border-border hover:bg-amber-50 hover:border-amber-200 transition-colors group"
                  >
                    <div className="font-semibold text-foreground group-hover:text-amber-700 text-sm">
                      Soft Delete
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Deactivate account — customer data is kept, login is
                      disabled.
                    </div>
                  </button>

                  <button
                    data-testid="hard-delete-btn"
                    onClick={() => handleDelete(true)}
                    disabled={deleteModal.deleting}
                    className="w-full text-left p-4 rounded-lg border border-red-200 hover:bg-red-50 transition-colors group"
                  >
                    <div className="font-semibold text-red-600 text-sm">
                      Hard Delete
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Permanently remove all customer data. This cannot be
                      undone.
                    </div>
                  </button>
                </div>
              </div>
              <div className="app-modal-footer flex items-center justify-end px-6 py-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeDeleteModal}
                  disabled={deleteModal.deleting}
                >
                  {deleteModal.deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Cancel"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default CustomersPage;
